export type ScorePopupKind = 'hit' | 'combo' | 'miss';

export interface ScorePopup {
    x: number;
    y: number;
    basePoints: number;
    comboBonus: number;
    comboMilestone: number | null;
    text: string;
    life: number;
    maxLife: number;
    kind: ScorePopupKind;
}

export function spawnScorePopup(
    x: number,
    y: number,
    basePoints: number,
    comboBonus = 0,
    comboMilestone: number | null = null
): ScorePopup {
    const isCombo = comboBonus > 0 && comboMilestone != null;
    return {
        x,
        y,
        basePoints,
        comboBonus,
        comboMilestone,
        text: `+${basePoints + comboBonus}`,
        life: isCombo ? 1100 : 750,
        maxLife: isCombo ? 1100 : 750,
        kind: isCombo ? 'combo' : 'hit',
    };
}

export function spawnMissPopup(x: number, y: number): ScorePopup {
    return {
        x,
        y,
        basePoints: 0,
        comboBonus: 0,
        comboMilestone: null,
        text: 'Raté !',
        life: 650,
        maxLife: 650,
        kind: 'miss',
    };
}

export function updateScorePopups(popups: ScorePopup[], deltaMs: number): ScorePopup[] {
    return popups
        .map((popup) => ({
            ...popup,
            y: popup.y - deltaMs * 0.06,
            life: popup.life - deltaMs,
        }))
        .filter((popup) => popup.life > 0);
}

function strokeAndFillText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    fillStyle: string
): void {
    ctx.strokeStyle = '#023047';
    ctx.lineWidth = 4;
    ctx.strokeText(text, x, y);
    ctx.fillStyle = fillStyle;
    ctx.fillText(text, x, y);
}

function drawComboPopup(
    ctx: CanvasRenderingContext2D,
    popup: ScorePopup,
    progress: number,
    alpha: number,
    scale: number
): void {
    const { basePoints, comboBonus, comboMilestone } = popup;
    const total = basePoints + comboBonus;
    const baseText = `+${basePoints}`;
    const bonusText = `+${comboBonus}`;
    const mergedText = `+${total}`;
    const milestoneLabel = comboMilestone != null ? `×${comboMilestone} !` : 'combo !';

    ctx.font = '600 26px "Bebas Neue", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = alpha;

    if (progress < 0.28) {
        ctx.save();
        ctx.scale(scale, scale);
        strokeAndFillText(ctx, baseText, 0, 0, '#ffb703');
        ctx.restore();
        return;
    }

    if (progress < 0.58) {
        const t = (progress - 0.28) / 0.3;
        const bonusX = 36 - t * 18;
        const bonusAlpha = Math.min(1, t * 1.4);
        ctx.save();
        ctx.scale(scale, scale);
        strokeAndFillText(ctx, baseText, -14, 0, '#ffb703');
        ctx.globalAlpha = alpha * bonusAlpha;
        strokeAndFillText(ctx, bonusText, bonusX, 0, '#fb8500');
        ctx.restore();
        return;
    }

    const mergeT = Math.min(1, (progress - 0.58) / 0.22);
    const tagAlpha = mergeT;
    ctx.save();
    ctx.scale(scale + mergeT * 0.08, scale + mergeT * 0.08);
    strokeAndFillText(ctx, mergedText, 0, -4, '#ffb703');
    ctx.font = '600 18px "Bebas Neue", sans-serif';
    ctx.globalAlpha = alpha * tagAlpha;
    strokeAndFillText(ctx, milestoneLabel, 0, 18, '#fb8500');
    ctx.restore();
}

export function drawScorePopups(ctx: CanvasRenderingContext2D, popups: ScorePopup[]): void {
    popups.forEach((popup) => {
        const progress = 1 - popup.life / popup.maxLife;
        const alpha = progress < 0.7 ? 1 : 1 - (progress - 0.7) / 0.3;
        const scale = 1 + Math.min(0.25, (1 - progress) * 0.35);

        ctx.save();
        ctx.translate(popup.x, popup.y);

        if (popup.kind === 'combo') {
            drawComboPopup(ctx, popup, progress, alpha, scale);
        } else {
            ctx.scale(scale, scale);
            ctx.globalAlpha = alpha;
            ctx.font = '600 26px "Bebas Neue", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const fill = popup.kind === 'miss' ? '#8d99ae' : '#ffb703';
            strokeAndFillText(ctx, popup.text, 0, 0, fill);
        }

        ctx.restore();
    });
    ctx.globalAlpha = 1;
}
